import { useNavigate } from 'react-router-dom';
import {
  Star, MapPin, Clock, ShieldCheck, Zap, Phone, ChevronRight, ArrowRight,
  Search, Wrench, CheckCircle2, Quote,
} from 'lucide-react';
import { useGoQuote } from './ServiceUI';

/* ============================================================
   Composants "élevés" pour les pages de service — pattern
   Trust & Authority + Conversion, dans l'ADN Allorep (#0052FF,
   Outfit/Jakarta, verre). Icônes Lucide uniquement, chiffres réels,
   contraste ≥ 4,5:1, motion à sens (respecte prefers-reduced-motion).
   ============================================================ */

const MAPS_URL = 'https://maps.app.goo.gl/X6hhYa4BE7uPptRVA';

/* --- iPhone rendu en code : device seul, fond transparent, net.
   Proportions réelles (~9:19.5), châssis titane, Dynamic Island, boutons.
   Remplaçable par une vraie photo via <img> quand disponible. --- */
export const IphoneMockup = ({ className = '', wallpaper, screenOff }) => (
  <div className={`relative w-[11.5rem] sm:w-[13rem] lg:w-[15rem] aspect-[41/86] ${className}`}>
    {/* châssis */}
    <div className="absolute inset-0 rounded-[2.7rem] bg-gradient-to-b from-slate-500 via-slate-700 to-slate-900 shadow-[0_35px_60px_-20px_rgba(2,6,23,0.55)] ring-1 ring-white/15 p-[3px]">
      {/* liseré noir */}
      <div className="w-full h-full rounded-[2.5rem] bg-slate-950 p-[7px]">
        {/* écran */}
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-slate-950">
          {wallpaper ? (
            <img src={wallpaper} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
          ) : screenOff ? null : (
            <>
              <div className="absolute inset-0" style={{ background: 'radial-gradient(125% 90% at 32% 12%, #7d97ff 0%, #3563ff 34%, #1b3aa8 66%, #0b1c56 100%)' }} />
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 w-[140%] h-36 bg-white/12 blur-3xl rounded-full" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/20" />
            </>
          )}
          {/* reflet de verre */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-white/10 pointer-events-none" />
          {/* Dynamic Island */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-[1.15rem] bg-black rounded-full" />
        </div>
      </div>
    </div>
    {/* boutons latéraux */}
    <div className="absolute -left-[2px] top-[24%] w-[3px] h-7 rounded-l bg-slate-800" />
    <div className="absolute -left-[2px] top-[34%] w-[3px] h-12 rounded-l bg-slate-800" />
    <div className="absolute -left-[2px] top-[47%] w-[3px] h-12 rounded-l bg-slate-800" />
    <div className="absolute -right-[2px] top-[30%] w-[3px] h-16 rounded-r bg-slate-800" />
  </div>
);

/* --- Samsung Galaxy rendu en code : bords fins, poinçon central, dalle AMOLED. --- */
export const SamsungMockup = ({ className = '', wallpaper, screenOff }) => (
  <div className={`relative w-[11.5rem] sm:w-[13rem] lg:w-[15rem] aspect-[40/87] ${className}`}>
    <div className="absolute inset-0 rounded-[2.2rem] bg-gradient-to-b from-slate-600 via-slate-800 to-slate-950 shadow-[0_35px_60px_-20px_rgba(2,6,23,0.55)] ring-1 ring-white/15 p-[2px]">
      <div className="w-full h-full rounded-[2.1rem] bg-slate-950 p-[4px]">
        <div className="relative w-full h-full rounded-[1.9rem] overflow-hidden bg-slate-950">
          {wallpaper ? (
            <img src={wallpaper} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
          ) : screenOff ? null : (
            <>
              <div className="absolute inset-0" style={{ background: 'radial-gradient(125% 90% at 68% 16%, #8ea6ff 0%, #3f63ff 32%, #1f2f96 66%, #0a1450 100%)' }} />
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 w-[140%] h-36 bg-cyan-200/12 blur-3xl rounded-full" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/25" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-white/10 pointer-events-none" />
          {/* poinçon caméra centré */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-black rounded-full ring-2 ring-black/40" />
        </div>
      </div>
    </div>
    <div className="absolute -right-[2px] top-[26%] w-[3px] h-10 rounded-r bg-slate-800" />
    <div className="absolute -right-[2px] top-[40%] w-[3px] h-14 rounded-r bg-slate-800" />
  </div>
);

/* --- Hero 2 colonnes : crédibilité à gauche, appareil à droite --- */
export const ServiceHeroPro = ({
  eyebrow = 'Réparation à Montpellier',
  h1Before, h1Highlight, h1After,
  intro, image, rating, hasWidget,
}) => {
  const goQuote = useGoQuote(hasWidget);
  return (
    <section className="relative overflow-hidden">
      {/* fond sobre + un seul halo maîtrisé */}
      <div className="absolute inset-0 gradient-section-blue" />
      <div className="absolute -top-24 right-0 w-[36rem] h-[36rem] bg-gradient-to-br from-blue-400/15 to-indigo-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10 py-10 lg:py-16">
        <div className={`grid gap-10 items-center ${image ? 'lg:grid-cols-2 lg:gap-8' : ''}`}>
          {/* Colonne texte */}
          <div className={`stagger-children ${image ? '' : 'max-w-3xl'}`}>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-blue-100 text-blue-700 font-semibold text-xs uppercase tracking-wider shadow-sm">
              <MapPin size={14} className="text-blue-500" />
              {eyebrow}
            </span>

            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-[3.25rem] leading-[1.05] font-bold text-slate-900">
              {h1Before}
              {h1Highlight && <span className="gradient-text">{h1Highlight}</span>}
              {h1After}
            </h1>

            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">{intro}</p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={goQuote}
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="hero-cta-devis"
              >
                Devis gratuit
                <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href="tel:+33983820754"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm text-slate-800 bg-white border border-slate-200 hover:border-blue-200 hover:bg-blue-50/40 transition-all"
              >
                <Phone size={16} className="text-emerald-500" />
                09&nbsp;83&nbsp;82&nbsp;07&nbsp;54
              </a>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={15} className="text-blue-500" />
                7 place des Martyrs de la Résistance, 34000 Montpellier
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={15} className="text-amber-500" />
                Lun–Sam 10h–20h · sans rendez-vous
              </span>
            </div>
          </div>

          {/* Colonne visuel */}
          {image && (
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* halo de marque derrière l'appareil */}
                <div className="absolute inset-0 scale-[0.8] bg-gradient-to-tr from-blue-500/30 via-indigo-500/20 to-transparent rounded-full blur-3xl" />
                <div className="absolute right-4 top-6 w-40 h-40 bg-cyan-400/20 rounded-full blur-3xl" />
                {image.node ? (
                  <div className="relative animate-float" role="img" aria-label={image.alt}>
                    {image.node}
                  </div>
                ) : (
                  /* fondu multiply : le fond blanc de la photo disparaît dans le hero */
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="relative w-[17rem] sm:w-[20rem] lg:w-[23rem] h-auto mix-blend-multiply animate-float"
                  />
                )}
                {rating && (
                  <div className="absolute -bottom-3 -left-3 sm:left-2 glass-card rounded-2xl px-4 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-0.5 star-rating">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                      ))}
                    </div>
                    <div className="leading-tight">
                      <p className="text-sm font-bold text-slate-900 tabular-nums">{rating.value}/5</p>
                      <p className="text-[11px] text-slate-500 tabular-nums">{rating.count} avis Google</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* --- Bande de preuve : signaux réels, icônes Lucide --- */
export const ProofStrip = ({ items }) => (
  <div className="border-y border-slate-100 bg-white/70">
    <div className="container-custom">
      <ul className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 divide-x divide-slate-100">
        {items.map((it, i) => (
          <li key={i} className={`flex items-center gap-3 py-4 px-4 ${i >= 4 ? 'hidden lg:flex' : ''}`}>
            <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
              <it.icon size={18} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-slate-800">{it.label}</p>
              {it.sub && <p className="text-xs text-slate-500">{it.sub}</p>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* --- Liste-diagnostic (pannes) : icône + intitulé + détail, 2 colonnes --- */
export const DiagnosisList = ({ title, intro, items }) => (
  <section className="container-custom py-12 lg:py-16">
    <div className="max-w-3xl">
      {title && <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">{title}</h2>}
      {intro && <p className="mt-3 text-slate-600 leading-relaxed">{intro}</p>}
    </div>
    <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-6">
      {items.map((it, i) => (
        <div key={i} className="flex gap-4">
          <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center text-blue-600">
            <it.icon size={20} strokeWidth={2} />
          </span>
          <div>
            <p className="font-semibold text-slate-900">{it.title}</p>
            <p className="mt-0.5 text-[15px] text-slate-600 leading-relaxed">{it.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* --- Timeline du déroulé : 4 étapes, ligne de liaison --- */
export const ProcessTimeline = ({ title, steps }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 gradient-section-purple" />
    <div className="container-custom relative z-10 py-12 lg:py-16">
      {title && <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 max-w-3xl">{title}</h2>}
      <ol className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
        {steps.map((s, i) => (
          <li key={i} className="relative">
            {/* connecteur (desktop) */}
            {i < steps.length - 1 && (
              <span className="hidden lg:block absolute top-6 left-[calc(50%+2.5rem)] right-[-2rem] h-px bg-gradient-to-r from-blue-200 to-transparent" />
            )}
            <div className="flex lg:flex-col items-start lg:items-center gap-4 lg:gap-3 lg:text-center">
              <span className="relative flex-shrink-0 w-12 h-12 rounded-2xl bg-white border border-blue-100 shadow-sm flex items-center justify-center text-blue-600">
                <s.icon size={20} strokeWidth={2} />
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                  {i + 1}
                </span>
              </span>
              <div>
                <p className="font-semibold text-slate-900">{s.title}</p>
                <p className="mt-0.5 text-sm text-slate-600">{s.sub}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

/* --- Carte tarifaire soignée, chiffres tabulaires --- */
export const PriceCardPro = ({ eyebrow, title, columns, rows, note, hasWidget }) => {
  const goQuote = useGoQuote(hasWidget);
  return (
    <section className="container-custom py-12 lg:py-16">
      <div className="max-w-3xl mb-8">
        {eyebrow && <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">{eyebrow}</p>}
        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">{title}</h2>
      </div>

      <div className="max-w-3xl rounded-3xl border border-slate-100 shadow-[0_20px_50px_-20px_rgba(0,82,255,0.18)] overflow-hidden bg-white">
        <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-indigo-600 px-6 py-4 grid grid-cols-[1fr_auto_auto] gap-4 text-white text-xs font-semibold uppercase tracking-wider">
          <span>{columns[0]}</span>
          <span className="text-right">{columns[1]}</span>
          <span className="text-right">{columns[2]}</span>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <div
              key={i}
              className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center px-6 py-3.5 ${i % 2 ? 'bg-slate-50/50' : 'bg-white'}`}
            >
              <span className="font-medium text-slate-800">{r[0]}</span>
              <span className="text-right text-sm text-slate-500 tabular-nums whitespace-nowrap">{r[1]}</span>
              <span className="justify-self-end inline-flex items-center rounded-full bg-blue-50 text-blue-700 font-semibold text-sm px-3 py-1 tabular-nums whitespace-nowrap">
                {r[2]}
              </span>
            </div>
          ))}
        </div>
        <div className="px-6 py-5 bg-slate-50/60 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-xs text-slate-500 max-w-md">
            {note || 'Prix de départ TTC — le devis final dépend de l’état de l’appareil. Devis gratuit en boutique.'}
          </p>
          <button
            onClick={goQuote}
            className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md shadow-blue-500/25 hover:-translate-y-0.5 transition-all"
          >
            Mon devis gratuit
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* --- Bande d'avis Google réels --- */
export const AvisStrip = ({ rating, count, reviews }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 gradient-section-blue" />
    <div className="container-custom relative z-10 py-12 lg:py-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">Ce que disent nos clients</h2>
          <div className="mt-2 flex items-center gap-2">
            <span className="flex items-center gap-0.5 star-rating">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}
            </span>
            <span className="text-sm text-slate-600 tabular-nums">
              <strong className="text-slate-900">{rating}/5</strong> · {count} avis Google
            </span>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {reviews.map((rv, i) => (
          <figure key={i} className="review-glass-card rounded-2xl p-6 flex flex-col">
            <Quote size={22} className="text-blue-300 mb-3" />
            <blockquote className="text-[15px] text-slate-700 leading-relaxed flex-1">{rv.text}</blockquote>
            <figcaption className="mt-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold flex items-center justify-center">
                {rv.name.charAt(0)}
              </span>
              <span className="text-sm font-semibold text-slate-800">{rv.name}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* --- Bande CTA locale : adresse + itinéraire + tél --- */
export const LocalCTA = ({ heading, sub }) => (
  <section className="container-custom py-12 lg:py-16">
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-700 px-6 py-10 lg:px-12 lg:py-12">
      <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="max-w-xl">
          <h2 className="text-2xl lg:text-3xl font-bold text-white">{heading}</h2>
          {sub && <p className="mt-2 text-blue-100">{sub}</p>}
          <p className="mt-4 inline-flex items-center gap-2 text-blue-100 text-sm">
            <MapPin size={16} />
            7 place des Martyrs de la Résistance, 34000 Montpellier · Lun–Sam 10h–20h
          </p>
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 flex-shrink-0">
          <a
            href="tel:+33983820754"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm text-blue-700 bg-white hover:bg-blue-50 transition-all shadow-lg"
          >
            <Phone size={16} className="text-emerald-500" />
            09&nbsp;83&nbsp;82&nbsp;07&nbsp;54
          </a>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm text-white bg-white/15 border border-white/30 hover:bg-white/25 transition-all"
          >
            <MapPin size={16} />
            Itinéraire
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* icônes prêtes à l'emploi pour les pages */
export { Search, Wrench, CheckCircle2, ShieldCheck, Zap, Clock, MapPin, Star };
